import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Upload, CheckCircle, Film, Link as LinkIcon, Scroll, Heart } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ContestLeaderboardPanel from "../components/ContestLeaderboardPanel";
import SponsorCrawl from "../components/SponsorCrawl";

const AGE_RANGES = ["18-21", "22-29", "30-39", "40-49", "50+"];
const GENRES = ["Hip-Hop", "R&B", "Pop", "Country", "Gospel", "Latin", "Rock", "EDM", "Afrobeats", "Jazz", "Acting", "Dance", "Comedy", "Other"];

/**
 * TalentContestPage — public landing + entry form for the $50k Next Top Creator contest.
 *
 * Unauthenticated users see the public landing + gallery of approved entries.
 * Logged-in users can submit their entry (or update their existing draft).
 * Rules must be e-signed (legal name typed + checkbox + 4 eligibility boxes).
 */
export default function TalentContestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [rules, setRules] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [myVotes, setMyVotes] = useState(new Set());
  const [myEntry, setMyEntry] = useState(null);
  const [tab, setTab] = useState("about");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showRules, setShowRules] = useState(false);

  const [form, setForm] = useState({
    stage_name: "", city: "", age_range: "", genres: [], bio: "", why_me: "",
    video_source_type: "upload", video_url: "",
    social_instagram: "", social_tiktok: "", social_youtube: "",
    rules_agreed: false, signed_name: "",
    eligibility_age: false, eligibility_us: false, eligibility_original: false, eligibility_travel: false,
  });
  const [videoFile, setVideoFile] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [i, g] = await Promise.all([api.get("/contest/info"), api.get("/contest/gallery")]);
      setInfo(i.data); setGallery(g.data || []);
      if (user) {
        try { const { data } = await api.get("/contest/entry/mine"); if (data?.id) { setMyEntry(data); populateFromEntry(data); } } catch {}
        try { const { data } = await api.get("/contest/vote/mine"); setMyVotes(new Set(data || [])); } catch {}
      } else {
        setMyVotes(new Set());
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const loadRules = async () => {
    try { const { data } = await api.get("/contest/rules"); setRules(data); } catch {}
  };
  useEffect(() => { load(); loadRules(); /* eslint-disable-next-line */ }, [user?.user_id]);

  const populateFromEntry = (e) => {
    setForm((f) => ({
      ...f,
      stage_name: e.stage_name || "", city: e.city || "", age_range: e.age_range || "",
      genres: e.genres || [], bio: e.bio || "", why_me: e.why_me || "",
      video_source_type: e.video_source_type || "upload",
      video_url: e.video_source_type === "upload" ? "" : (e.video_url || ""),
      social_instagram: e.social_instagram || "", social_tiktok: e.social_tiktok || "",
      social_youtube: e.social_youtube || "",
    }));
  };

  const toggleGenre = (g) => setForm((f) => {
    const has = f.genres.includes(g);
    return { ...f, genres: has ? f.genres.filter((x) => x !== g) : [...f.genres, g].slice(0, 4) };
  });

  const castVote = async (entryId) => {
    if (!user) { toast.error("Sign in to vote"); navigate("/login"); return; }
    const voted = myVotes.has(entryId);
    // Optimistic UI update
    setMyVotes((s) => { const n = new Set(s); voted ? n.delete(entryId) : n.add(entryId); return n; });
    setGallery((g) => g.map((e) => e.id === entryId ? { ...e, votes_count: Math.max(0, (e.votes_count || 0) + (voted ? -1 : 1)) } : e));
    try {
      const { data } = voted
        ? await api.delete(`/contest/vote/${entryId}`)
        : await api.post(`/contest/vote/${entryId}`);
      // Reconcile count with server truth
      setGallery((g) => g.map((e) => e.id === entryId ? { ...e, votes_count: data.count ?? e.votes_count } : e));
    } catch (e) {
      // Roll back on failure
      setMyVotes((s) => { const n = new Set(s); voted ? n.add(entryId) : n.delete(entryId); return n; });
      toast.error(formatApiError(e));
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!user) { navigate("/login"); return; }
    if (!form.rules_agreed) { toast.error("Please agree to the Official Rules"); return; }
    if (!form.signed_name.trim()) { toast.error("Type your legal name as your e-signature"); return; }
    if (!form.eligibility_age || !form.eligibility_us || !form.eligibility_original || !form.eligibility_travel) {
      toast.error("All four eligibility boxes must be checked"); return;
    }
    if (form.why_me.trim().length < 40) { toast.error("Your pitch must be at least 40 characters"); return; }
    if (form.video_source_type === "upload" && !videoFile && !myEntry) { toast.error("Upload your video (or switch to a YouTube/TikTok link)"); return; }
    if (form.video_source_type !== "upload" && !form.video_url.startsWith("http")) { toast.error("Paste a valid YouTube or TikTok URL"); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "genres") fd.append("genres", v.join(","));
        else if (typeof v === "boolean") fd.append(k, v ? "true" : "false");
        else fd.append(k, v ?? "");
      });
      fd.append("rules_version", rules?.version || "v1.0");
      if (form.video_source_type === "upload" && videoFile) fd.append("video", videoFile);
      const tok = localStorage.getItem("tv_token") || "";
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contest/entry`, {
        method: "POST", body: fd,
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const saved = await res.json();
      setMyEntry(saved);
      toast.success("Your entry is in — admins will review and contact you.");
      setTab("about");
      setVideoFile(null); load();
    } catch (e) { toast.error(e.message || String(e)); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#05060a] text-white flex items-center justify-center">Loading contest…</div>;

  const prizeDisplay = info?.prize_usd ? `$${Number(info.prize_usd).toLocaleString()}` : "$50,000";

  return (
    <div data-testid="talent-contest-page" className="min-h-screen bg-[#05060a] text-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-fuchsia-600/20 via-purple-700/10 to-tv-accent/10 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 pt-28 md:pt-32 pb-12">
          <div className="inline-flex items-center gap-2 bg-tv-accent/20 border border-tv-accent/40 text-tv-accent text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4">
            <Trophy className="w-3 h-3" /> Tap IN TV · Official contest
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl leading-tight">
            <span className="text-red-400">{prizeDisplay}</span> Next Top Creator
          </h1>
          <p className="text-white/70 mt-3 max-w-2xl text-lg">
            One creator. One grand prize. Submit your pitch, upload a short video of what makes you <em>undeniable</em>, and get in front of the Tap IN TV Network.
          </p>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <button
              data-testid="contest-enter-btn"
              onClick={() => user ? setTab("enter") : navigate("/login")}
              className="bg-tv-accent hover:bg-yellow-400 text-black font-black text-lg px-6 py-3 rounded-full"
            >
              {myEntry ? "Update my entry" : "Enter the contest"}
            </button>
            <button onClick={() => setShowRules(true)} data-testid="contest-view-rules" className="bg-white/10 hover:bg-white/20 text-white font-black px-5 py-3 rounded-full inline-flex items-center gap-2">
              <Scroll className="w-4 h-4" /> Read official rules
            </button>
          </div>
          {myEntry && (
            <div className="mt-4 inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-sm font-black px-3 py-1.5 rounded-full" data-testid="contest-entry-status">
              <CheckCircle className="w-4 h-4" /> Your entry · {myEntry.status}
            </div>
          )}
        </div>
      </section>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-6 py-4 flex gap-2 border-b border-white/10">
        <TabBtn active={tab === "about"} onClick={() => setTab("about")} testid="contest-tab-about">About & Gallery</TabBtn>
        <TabBtn active={tab === "enter"} onClick={() => setTab("enter")} testid="contest-tab-enter">{myEntry ? "Update entry" : "Enter contest"}</TabBtn>
      </div>

      {tab === "about" && (
        <section className="max-w-5xl mx-auto px-6 py-8 space-y-8" data-testid="contest-about">
          <ContestLeaderboardPanel />
          <div>
          <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
            <h2 className="font-display font-black text-2xl">Vote for your favorite</h2>
            <div className="text-white/50 text-sm">Sign in to cast a vote · one vote per contestant · unlimited contestants</div>
          </div>
          {gallery.length === 0 ? (
            <p className="text-white/50">The gallery opens once the first entries are approved. Check back soon.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.map((g) => {
                const voted = myVotes.has(g.id);
                return (
                  <div key={g.id} data-testid={`gallery-${g.id}`} className="bg-white/5 border border-white/10 rounded-lg overflow-hidden flex flex-col">
                    <div className="aspect-video bg-black flex items-center justify-center text-white/30">
                      <Film className="w-8 h-8" />
                    </div>
                    <div className="p-3 flex-1">
                      <div className="flex items-center gap-1.5">
                        <div className="font-bold truncate flex-1">{g.stage_name}</div>
                        {g.status === "winner" && <span className="bg-tv-accent text-black text-[9px] font-black px-1.5 py-0.5 rounded">WINNER</span>}
                        {g.status === "finalist" && <span className="bg-fuchsia-500 text-black text-[9px] font-black px-1.5 py-0.5 rounded">FINALIST</span>}
                      </div>
                      <div className="text-white/60 text-xs mt-0.5 line-clamp-2">{g.bio}</div>
                    </div>
                    <div className="px-3 pb-3 flex items-center justify-between">
                      <div className="inline-flex items-center gap-1 text-white/70 text-xs font-bold" data-testid={`gallery-count-${g.id}`}>
                        <Heart className="w-3.5 h-3.5 text-fuchsia-400" /> {g.votes_count || 0} votes
                      </div>
                      <button
                        onClick={() => castVote(g.id)}
                        data-testid={`gallery-vote-${g.id}`}
                        disabled={g.status === "winner"}
                        className={`text-xs font-black px-3 py-1.5 rounded inline-flex items-center gap-1 transition ${
                          voted
                            ? "bg-fuchsia-500 text-black hover:bg-fuchsia-400"
                            : "bg-white/10 text-white hover:bg-fuchsia-500/30 border border-fuchsia-500/40"
                        } ${g.status === "winner" ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <Heart className={`w-3.5 h-3.5 ${voted ? "fill-black" : ""}`} />
                        {voted ? "Voted" : "Vote"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </section>
      )}

      {tab === "enter" && (
        <section className="max-w-3xl mx-auto px-6 py-8" data-testid="contest-enter">
          {!user ? (
            <div className="bg-amber-500/10 border border-amber-500/40 rounded p-4">
              <div className="font-black text-amber-200">Sign in to enter.</div>
              <div className="text-white/70 text-sm mt-1">Your entry is tied to your Tap IN account so we can contact you if you advance.</div>
              <button onClick={() => navigate("/login")} className="mt-3 bg-tv-accent hover:bg-yellow-400 text-black font-black px-4 py-2 rounded">Sign in / Sign up</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="font-display font-black text-2xl">Your entry</h2>
              {myEntry?.status && myEntry.status !== "draft" && (
                <div className="bg-white/5 border border-white/10 rounded p-3 text-sm text-white/70">
                  Status: <strong className="text-white">{myEntry.status}</strong>. You can update the form below and re-submit anytime before the contest closes.
                </div>
              )}

              <Field label="Stage name *">
                <input data-testid="contest-stage-name" value={form.stage_name} onChange={(e) => setForm({ ...form, stage_name: e.target.value })} className={inputCls} required />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="City / State">
                  <input data-testid="contest-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Age range">
                  <select data-testid="contest-age" value={form.age_range} onChange={(e) => setForm({ ...form, age_range: e.target.value })} className={inputCls}>
                    <option value="">—</option>
                    {AGE_RANGES.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Genre / lane (pick up to 4)">
                <div className="flex flex-wrap gap-1.5" data-testid="contest-genres">
                  {GENRES.map((g) => (
                    <button key={g} type="button" onClick={() => toggleGenre(g)} className={`text-xs font-bold px-2.5 py-1 rounded-full border ${form.genres.includes(g) ? "bg-tv-accent text-black border-tv-accent" : "bg-white/5 text-white/70 border-white/10"}`}>{g}</button>
                  ))}
                </div>
              </Field>
              <Field label="Short bio">
                <textarea data-testid="contest-bio" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={inputCls} />
              </Field>
              <Field label={`Why are YOU the next top creator? (min 40 chars — ${form.why_me.length})`}>
                <textarea data-testid="contest-why" rows={5} value={form.why_me} onChange={(e) => setForm({ ...form, why_me: e.target.value })} className={inputCls} required />
              </Field>

              <Field label="Your audition video *">
                <div className="flex gap-2 mb-2">
                  {[["upload","Upload file"],["youtube","YouTube"],["tiktok","TikTok"]].map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setForm({ ...form, video_source_type: k })} data-testid={`contest-video-kind-${k}`} className={`text-xs font-bold px-3 py-1.5 rounded ${form.video_source_type === k ? "bg-tv-accent text-black" : "bg-white/5 text-white/70"}`}>{label}</button>
                  ))}
                </div>
                {form.video_source_type === "upload" ? (
                  <div className="flex items-center gap-2 bg-white/5 border border-dashed border-white/20 rounded p-3">
                    <Upload className="w-5 h-5 text-tv-accent" />
                    <input data-testid="contest-video-file" type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} className="flex-1 text-sm text-white/80" />
                    {videoFile && <span className="text-white/60 text-xs">{(videoFile.size / 1e6).toFixed(1)} MB</span>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <LinkIcon className="w-5 h-5 text-white/50" />
                    <input data-testid="contest-video-url" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder={form.video_source_type === "youtube" ? "https://youtube.com/watch?v=..." : "https://tiktok.com/@me/video/..."} className={inputCls} />
                  </div>
                )}
                <div className="text-white/50 text-[11px] mt-1">Keep it under 5 minutes. Original content only.</div>
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Instagram"><input data-testid="contest-ig" value={form.social_instagram} onChange={(e) => setForm({ ...form, social_instagram: e.target.value })} placeholder="@handle" className={inputCls} /></Field>
                <Field label="TikTok"><input data-testid="contest-tt" value={form.social_tiktok} onChange={(e) => setForm({ ...form, social_tiktok: e.target.value })} placeholder="@handle" className={inputCls} /></Field>
                <Field label="YouTube"><input data-testid="contest-yt" value={form.social_youtube} onChange={(e) => setForm({ ...form, social_youtube: e.target.value })} placeholder="channel URL" className={inputCls} /></Field>
              </div>

              {/* Eligibility + Rules e-sign */}
              <div className="bg-fuchsia-500/5 border border-fuchsia-500/30 rounded-lg p-4 space-y-2.5">
                <div className="font-black text-fuchsia-200 uppercase tracking-wider text-xs">Eligibility (all required)</div>
                <CheckRow testid="contest-elig-age" checked={form.eligibility_age} onChange={(v) => setForm({ ...form, eligibility_age: v })}>I am at least 18 years old.</CheckRow>
                <CheckRow testid="contest-elig-us" checked={form.eligibility_us} onChange={(v) => setForm({ ...form, eligibility_us: v })}>I am a legal resident of the United States or its territories.</CheckRow>
                <CheckRow testid="contest-elig-original" checked={form.eligibility_original} onChange={(v) => setForm({ ...form, eligibility_original: v })}>The video and bio I'm submitting are my own original work (or fully cleared).</CheckRow>
                <CheckRow testid="contest-elig-travel" checked={form.eligibility_travel} onChange={(v) => setForm({ ...form, eligibility_travel: v })}>If I advance, I can travel to an in-person finals event.</CheckRow>
                <hr className="border-white/10" />
                <CheckRow testid="contest-rules-agreed" checked={form.rules_agreed} onChange={(v) => setForm({ ...form, rules_agreed: v })}>
                  I have read and agree to the <button type="button" onClick={() => setShowRules(true)} className="underline text-tv-accent">Official Rules (v1.0)</button>.
                </CheckRow>
                <Field label="E-signature — type your legal name *">
                  <input data-testid="contest-signed-name" value={form.signed_name} onChange={(e) => setForm({ ...form, signed_name: e.target.value })} placeholder="First Middle Last" className={inputCls} required />
                </Field>
                <div className="text-white/40 text-[10px]">Your IP and browser agent are recorded alongside your signature as proof of consent.</div>
              </div>

              <button
                type="submit"
                data-testid="contest-submit"
                disabled={submitting}
                className="w-full bg-tv-accent hover:bg-yellow-400 disabled:opacity-40 text-black font-black text-lg py-3 rounded"
              >{submitting ? "Submitting…" : (myEntry ? "Update my entry" : "Submit entry")}</button>
            </form>
          )}
        </section>
      )}

      {/* Rules modal */}
      {showRules && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setShowRules(false)} data-testid="contest-rules-modal">
          <div className="bg-[#0b0e14] border border-white/10 rounded-lg max-w-3xl max-h-[85vh] overflow-y-auto w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Scroll className="w-5 h-5 text-tv-accent" />
              <h2 className="font-display font-black text-xl flex-1">Official Rules · {rules?.version || "v1.0"}</h2>
              <button onClick={() => setShowRules(false)} className="text-white/60 hover:text-white">✕</button>
            </div>
            <pre className="whitespace-pre-wrap text-white/80 text-sm font-sans leading-relaxed">{rules?.text || "Loading rules…"}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white focus:border-tv-accent outline-none";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-black text-white/60 mb-1">{label}</div>
      {children}
    </label>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-4 py-2 font-bold text-sm ${active ? "text-tv-accent border-b-2 border-tv-accent" : "text-white/60 hover:text-white"}`}>{children}</button>
  );
}

function CheckRow({ checked, onChange, children, testid }) {
  return (
    <label className="flex items-start gap-2 text-sm text-white/80 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5 accent-tv-accent w-4 h-4" />
      <span>{children}</span>
    </label>
  );
}
